import { useEffect, useRef, useState } from "react";
import { useMutation } from "react-query";

const usePagination = (
  setTableData,
  mutationFn,
  defaultValues,
  editDependencies = [],
  initialFetch = false
) => {
  const [itemsTotal, setItemsTotal] = useState(0);
  const [limit, setLimit] = useState(defaultValues?.limit || 10);
  const [skip, setSkip] = useState(defaultValues?.skip || 0);
  const [filters, setFilters] = useState();
  const [sorting, setSorting] = useState();
  const appendedFiltersKeys = useRef([]);

  const [storedData, setStoredData] = useState([]);

  const fetchData = () => {
    const itemsSlice = storedData.filter(
      (u) => u.index >= skip && u.index < skip + limit
    );

    const isLastPage = itemsTotal <= skip + limit;

    if (
      (itemsSlice.length !== limit && !isLastPage) ||
      itemsSlice.length === 0
    ) {
      findDataMutation.mutate();
    } else {
      successHandler(itemsSlice, [{ total: itemsTotal }]);
    }
  };

  const findDataMutation = useMutation({
    mutationFn: () => mutationFn({ limit, skip, ...sorting, ...filters }),
    onSuccess: ({ data: { data, metadata } }) => {
      successHandler(
        data.map((d, i) => ({ data: d, index: skip + i })),
        metadata
      );
    },
  });

  const successHandler = (data, metadata) => {
    const sliceToShow = data.filter(
      (d) => d.index >= skip && d.index < skip + limit
    );

    const total = metadata?.[0]?.total ?? 0;
    setItemsTotal(total);
    setTableData(sliceToShow);
    setStoredData((prev) => handleNewStoredData(data, [...prev]));
  };

  const handleNewStoredData = (data, prev) => {
    const itemsSlice = prev.filter(
      (u) => u.index >= skip && u.index < skip + limit
    );

    data.forEach((d, i) => {
      const prevItem = itemsSlice.find((u) => u.index === skip + i);
      if (!prevItem) prev.push(d);
      else prevItem.data = d.data;
    });
    return prev;
  };

  const filtersChangeHandler = (filtersArr, append = false) => {
    if (
      (!filters && filtersArr.length === 0) ||
      (filtersArr.length === 0 &&
        append &&
        appendedFiltersKeys.current.length === 0)
    ) {
      return;
    }

    const obj = {};
    filtersArr.forEach((f) => {
      obj[f.id] = f.value;
    });

    if (append) {
      setFilters((prev) => {
        appendedFiltersKeys.current.forEach((key) => {
          delete prev[key];
        });
        return {
          ...prev,
          ...obj,
        };
      });
      appendedFiltersKeys.current = filtersArr.map((f) => f.id);
    } else {
      setFilters(filtersArr.length === 0 ? undefined : obj);
    }

    setStoredData([]);
  };

  const sortingChangeHandler = (sortingArr) => {
    if (!sorting && sortingArr.length === 0) return;

    setSorting(
      sortingArr?.[0] && {
        sort_by: sortingArr[0].id,
        order: sortingArr[0].desc ? -1 : 1,
      }
    );
    setStoredData([]);
  };

  const isLoading = findDataMutation.isLoading;

  const isInitialFetch = useRef(initialFetch);

  useEffect(() => {
    if (!isInitialFetch.current) {
      isInitialFetch.current = true;
      return;
    }

    // checking if not editing item etc.
    if (editDependencies.some((d) => !!d)) return;

    fetchData();
  }, [limit, skip, filters, sorting, ...editDependencies]);

  return {
    itemsTotal,
    filtersChangeHandler,
    sortingChangeHandler,
    isLoading,
    setLimit,
    limit,
    setSkip,
    skip,
    refetch: findDataMutation.mutate,
  };
};

export default usePagination;
